/**
 * TaskEstimateStore — the Sprint Board estimate feature's data-access wrapper.
 *
 * Strict layering: talks to the API only through the {@link EngineeringVectorApi}
 * facade (`api.documents<TaskEstimate>('taskEstimates')` + `api.identityProvider`).
 * Never imports ApiClient or calls fetch. React hooks call this store; pages
 * call the hook.
 *
 * Persistence model: each estimate is one document with id `taskest_<uuid>`.
 * The renderable / queryable fields (taskId, roomId, headline numbers, source,
 * timestamps, deleted) are mirrored onto the DDB pointer row so a task's
 * estimate can be located by taskId WITHOUT hydrating S3 bodies. The full engine
 * inputs (complexity[]/extraWork[]) and the transparent breakdown live in the S3
 * body and are read only on the detail view via {@link get}.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 */

import type { EngineeringVectorApi } from '../../../api';
import type { DocumentStore } from '../../../api';
import type { StringMap } from '../../../api';
import type { ListMetaInfoQuery } from '../../../api';
import type {
  ChangeType,
  ComplexityFactor,
  EstimateBreakdownItem,
  ExtraWorkItem,
  Unknowns,
} from '../engine/estimation';
import type { EstimateSource, TaskEstimate, TaskEstimateDraft } from './types';
import {
  coerceComplexity,
  coerceExtraWork,
  isChangeType,
  isUnknowns,
  taskEstimateDocId,
} from './taskEstimateModel';

export const TASK_ESTIMATES_COLLECTION = 'taskEstimates';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  roomId: 'roomId',
  taskId: 'taskId',
  changeType: 'changeType',
  unknowns: 'unknowns',
  effortDays: 'effortDays',
  storyPoints: 'storyPoints',
  confidence: 'confidence',
  isSpike: 'isSpike',
  source: 'source',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  deleted: 'deleted',
} as const;

export class TaskEstimateStore {
  private readonly docs: DocumentStore<TaskEstimate>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<TaskEstimate>(TASK_ESTIMATES_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(e: TaskEstimate): StringMap {
    return {
      [ROW.roomId]: e.roomId ?? '',
      [ROW.taskId]: e.taskId ?? '',
      [ROW.changeType]: e.changeType,
      [ROW.unknowns]: e.unknowns,
      [ROW.effortDays]: String(e.effortDays),
      [ROW.storyPoints]: String(e.storyPoints),
      [ROW.confidence]: String(e.confidence),
      [ROW.isSpike]: e.isSpike ? 'true' : 'false',
      [ROW.source]: e.source,
      [ROW.createdBy]: e.createdBy ?? '',
      [ROW.createdAt]: e.createdAt ?? '',
      [ROW.updatedAt]: e.updatedAt ?? '',
      [ROW.deleted]: e.deleted ? 'true' : 'false',
    };
  }

  /**
   * Creates a new estimate for a task. Seeds id/roomId/createdBy/timestamps and
   * returns the saved document.
   */
  async create(
    roomId: string,
    draft: TaskEstimateDraft,
  ): Promise<TaskEstimate> {
    const identity = await this.api.identityProvider.getIdentity();
    const now = new Date().toISOString();
    const estimate: TaskEstimate = {
      id: taskEstimateDocId(),
      roomId,
      taskId: draft.taskId,
      changeType: draft.changeType,
      complexity: coerceComplexity(draft.complexity),
      extraWork: coerceExtraWork(draft.extraWork),
      unknowns: draft.unknowns,
      customChangeType: draft.customChangeType,
      customBaseDays: draft.customBaseDays,
      breakdown: draft.breakdown ?? [],
      effortDays: draft.effortDays,
      storyPoints: draft.storyPoints,
      confidence: draft.confidence,
      isSpike: draft.isSpike,
      timeboxDays: draft.timeboxDays,
      source: draft.source,
      rationale: draft.rationale,
      sources: draft.sources,
      override: draft.override,
      createdBy: identity.alias,
      createdAt: now,
      updatedAt: now,
      deleted: false,
    };
    await this.docs.save(estimate.id, estimate, this.indexAttributes(estimate));
    return estimate;
  }

  /** Loads a single estimate document by id (hydrates inputs + breakdown). */
  async get(id: string): Promise<TaskEstimate | undefined> {
    if (!id) return undefined;
    return this.docs.get(id);
  }

  /**
   * Applies a new set of engine inputs/outputs to an existing estimate in place
   * (same id/taskId/roomId/createdBy), refreshes updatedAt, and re-mirrors index
   * attributes. Used when an engineer edits an existing estimate.
   */
  async applyDraft(
    estimate: TaskEstimate,
    draft: TaskEstimateDraft,
  ): Promise<TaskEstimate> {
    const updated: TaskEstimate = {
      ...estimate,
      changeType: draft.changeType,
      complexity: coerceComplexity(draft.complexity),
      extraWork: coerceExtraWork(draft.extraWork),
      unknowns: draft.unknowns,
      customChangeType: draft.customChangeType,
      customBaseDays: draft.customBaseDays,
      breakdown: draft.breakdown ?? [],
      effortDays: draft.effortDays,
      storyPoints: draft.storyPoints,
      confidence: draft.confidence,
      isSpike: draft.isSpike,
      timeboxDays: draft.timeboxDays,
      source: draft.source,
      rationale: draft.rationale,
      sources: draft.sources,
      override: draft.override,
      updatedAt: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Lists a single task's non-deleted estimates (newest first). Issues a
   * taskId-filtered listMetaInfo query, then re-filters client-side (collection
   * + taskId + not-deleted) so the result is correct even if the backend ignores
   * the filter expression. Results are reconstructed from pointer rows (headline
   * numbers only — the full breakdown requires {@link get}).
   */
  async listByTask(taskId: string): Promise<TaskEstimate[]> {
    if (!taskId) return [];
    const query: ListMetaInfoQuery = {
      filterExpression: '#taskId = :taskId AND #deleted <> :deleted',
      expressionAttributeNames: {
        '#taskId': ROW.taskId,
        '#deleted': ROW.deleted,
      },
      expressionAttributeValues: {
        ':taskId': taskId,
        ':deleted': 'true',
      },
    };
    const rows = await this.docs.list(query);
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TASK_ESTIMATES_COLLECTION &&
          r[ROW.taskId] === taskId &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toEstimate(r))
      .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
  }

  /** Soft-deletes an estimate (flags the document + pointer row). */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: TaskEstimate = {
      ...existing,
      deleted: true,
      updatedAt: new Date().toISOString(),
    };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }

  /** Parses an optional numeric pointer-row attribute (empty → 0). */
  private num(raw: string | undefined): number {
    if (raw == null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  /**
   * Reconstructs a partial TaskEstimate from a pointer row. The inputs
   * arrays + breakdown are NOT on the row, so they come back empty — callers
   * that need the full breakdown call {@link get} to hydrate the S3 body.
   */
  private toEstimate(row: StringMap): TaskEstimate {
    const changeType = row[ROW.changeType] ?? '';
    const unknowns = row[ROW.unknowns] ?? '';
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      roomId: row[ROW.roomId] ?? '',
      taskId: row[ROW.taskId] ?? '',
      changeType: (isChangeType(changeType)
        ? changeType
        : 'feature') as ChangeType,
      complexity: [] as ComplexityFactor[],
      extraWork: [] as ExtraWorkItem[],
      unknowns: (isUnknowns(unknowns) ? unknowns : 'well_understood') as Unknowns,
      breakdown: [] as EstimateBreakdownItem[],
      effortDays: this.num(row[ROW.effortDays]),
      storyPoints: this.num(row[ROW.storyPoints]),
      confidence: this.num(row[ROW.confidence]),
      isSpike: row[ROW.isSpike] === 'true',
      source: (row[ROW.source] as EstimateSource) || 'manual',
      createdBy: row[ROW.createdBy] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
      updatedAt: row[ROW.updatedAt] ?? '',
      deleted: row[ROW.deleted] === 'true',
    };
  }
}
